// Real-time Firebase System
// Handles live updates between admin panel and frontend

import { 
  doc, 
  onSnapshot, 
  collection, 
  query, 
  where, 
  orderBy, 
  limit,
  writeBatch,
  runTransaction,
  addDoc
} from 'firebase/firestore';
import { db } from './config';
import { COLLECTIONS } from './collections';

// Real-time listeners cache
const listeners = new Map();

// ===== REAL-TIME USER DATA LISTENERS =====

// Listen to user dashboard data in real-time
export const listenToUserDashboard = (userId, callback) => {
  const unsubscribe = onSnapshot(
    doc(db, 'users', userId),
    (doc) => {
      if (doc.exists()) {
        callback({ success: true, data: doc.data() });
      } else {
        callback({ success: false, error: 'User not found' });
      }
    },
    (error) => {
      callback({ success: false, error });
    }
  );

  // Store listener for cleanup
  listeners.set(`user-dashboard-${userId}`, unsubscribe);
  return unsubscribe;
};

// Listen to notes in real-time
export const listenToNotes = (callback) => {
  const q = query(
    collection(db, COLLECTIONS.NOTES),
    orderBy('createdAt', 'desc')
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const notes = [];
    snapshot.forEach((doc) => {
      notes.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: notes });
  }, (error) => {
    callback({ success: false, error });
  });

  listeners.set('notes', unsubscribe);
  return unsubscribe;
};

// Listen to user test results in real-time
export const listenToUserTestResults = (userId, limitCount = 10, callback) => {
  // First try with the composite index
  const q = query(
    collection(db, COLLECTIONS.TEST_RESULTS),
    where('userId', '==', userId),
    orderBy('completedAt', 'desc'),
    limit(limitCount)
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const testResults = [];
    snapshot.forEach((doc) => {
      testResults.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: testResults });
  }, (error) => {
    // Check if it's an index error
    if (error.code === 'failed-precondition' || error.message.includes('index')) {
      // Fallback to simple query without ordering
      const fallbackQuery = query(
        collection(db, COLLECTIONS.TEST_RESULTS),
        where('userId', '==', userId),
        limit(limitCount)
      );
      
      const fallbackUnsubscribe = onSnapshot(fallbackQuery, (snapshot) => {
        const testResults = [];
        snapshot.forEach((doc) => {
          testResults.push({ id: doc.id, ...doc.data() });
        });
        
        // Sort in memory by completedAt
        testResults.sort((a, b) => {
          const dateA = a.completedAt ? new Date(a.completedAt) : new Date(0);
          const dateB = b.completedAt ? new Date(b.completedAt) : new Date(0);
          return dateB - dateA;
        });
        
        callback({ success: true, data: testResults, usedFallback: true });
      }, (fallbackError) => {
        callback({ success: false, error: fallbackError });
      });
      
      // Store the fallback unsubscribe function
      listeners.set(`user-test-results-fallback-${userId}`, fallbackUnsubscribe);
      return;
    }
    
    callback({ success: false, error });
  });

  listeners.set(`user-test-results-${userId}`, unsubscribe);
  return unsubscribe;
};

// Listen to user preferences in real-time
export const listenToUserPreferences = (userId, callback) => {
  const docRef = doc(db, 'userPreferences', userId);

  const unsubscribe = onSnapshot(docRef, (snapshot) => {
    if (snapshot.exists()) {
      const preferences = snapshot.data();
      callback({ success: true, data: preferences });
    } else {
      // Create default preferences if they don't exist
      const defaultPreferences = {
        examType: '',
        subjects: [],
        notifications: true,
        emailNotifications: true,
        pushNotifications: true,
        theme: 'light',
        language: 'en',
        timezone: 'UTC',
        selectedExams: []
      };
      callback({ success: true, data: defaultPreferences });
    }
  }, (error) => {
    callback({ success: false, error });
  });

  listeners.set(`user-preferences-${userId}`, unsubscribe);
  return unsubscribe;
};

// Listen to user notes in real-time
export const listenToUserNotes = (userId, limitCount = 100, callback) => {
  const q = query(
    collection(db, COLLECTIONS.NOTES),
    where('isPublic', '==', true),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const notes = [];
    snapshot.forEach((doc) => {
      notes.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: notes });
  }, (error) => {
    callback({ success: false, error });
  });

  listeners.set(`user-notes-${userId}`, unsubscribe);
  return unsubscribe;
};

// Listen to user doubts in real-time
export const listenToUserDoubts = (userId, limitCount = 50, callback) => {
  const q = query(
    collection(db, COLLECTIONS.DOUBTS),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const doubts = [];
    snapshot.forEach((doc) => {
      doubts.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: doubts });
  }, (error) => {
    callback({ success: false, error });
  });

  listeners.set(`user-doubts-${userId}`, unsubscribe);
  return unsubscribe;
};

// Listen to user activities in real-time
export const listenToUserActivities = (userId, limitCount = 100, callback) => {
  const q = query(
    collection(db, COLLECTIONS.USER_PROGRESS),
    where('userId', '==', userId),
    orderBy('timestamp', 'desc'),
    limit(limitCount)
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const activities = [];
    snapshot.forEach((doc) => {
      activities.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: activities });
  }, (error) => {
    callback({ success: false, error });
  });

  listeners.set(`user-activities-${userId}`, unsubscribe);
  return unsubscribe;
};

// ===== REAL-TIME ADMIN DATA LISTENERS =====

// Listen to all users in real-time (admin)
export const listenToAllUsers = (callback) => {
  const q = query(collection(db, COLLECTIONS.USERS), orderBy('createdAt', 'desc'));

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const users = [];
    snapshot.forEach((doc) => {
      users.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: users });
  }, (error) => {
    callback({ success: false, error });
  });

  listeners.set('all-users', unsubscribe);
  return unsubscribe;
};

// Listen to all test results in real-time (admin)
export const listenToAllTestResults = (callback) => {
  const q = query(collection(db, COLLECTIONS.TEST_RESULTS), orderBy('createdAt', 'desc'));

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const testResults = [];
    snapshot.forEach((doc) => {
      testResults.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: testResults });
  }, (error) => {
    callback({ success: false, error });
  });

  listeners.set('all-test-results', unsubscribe);
  return unsubscribe;
};

// Listen to all questions in real-time (admin)
export const listenToAllQuestions = (callback) => {
  const q = query(collection(db, COLLECTIONS.QUESTION_BANK), orderBy('createdAt', 'desc'));

  const unsubscribe = onSnapshot(q, (snapshot) => {
    try {
      const questions = [];
      snapshot.forEach((doc) => {
        questions.push({ id: doc.id, ...doc.data() });
      });
      callback({ success: true, data: questions });
    } catch (error) {
      callback({ success: true, data: [] }); // Return empty array instead of failing
    }
  }, (error) => {
    callback({ success: false, error });
  });

  listeners.set('all-questions', unsubscribe);
  return unsubscribe;
};

// Listen to all courses in real-time (admin)
export const listenToAllCourses = (callback) => {
  const q = query(collection(db, COLLECTIONS.COURSES), orderBy('createdAt', 'desc'));

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const courses = [];
    snapshot.forEach((doc) => {
      courses.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: courses });
  }, (error) => {
    callback({ success: false, error });
  });

  listeners.set('all-courses', unsubscribe);
  return unsubscribe;
};

// Listen to all doubts in real-time (admin)
export const listenToAllDoubts = (callback) => {
  const q = query(collection(db, COLLECTIONS.DOUBTS), orderBy('createdAt', 'desc'));

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const doubts = [];
    snapshot.forEach((doc) => {
      doubts.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: doubts });
  }, (error) => {
    callback({ success: false, error });
  });

  listeners.set('all-doubts', unsubscribe);
  return unsubscribe;
};

// ===== REAL-TIME COURSE DATA LISTENERS =====

// Listen to course assignments in real-time
export const listenToCourseAssignments = (courseId, callback) => {
  const q = query(
    collection(db, 'courses', courseId, 'assignments'),
    orderBy('dueDate', 'asc')
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const assignments = [];
    snapshot.forEach((doc) => {
      assignments.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: assignments });
  }, (error) => {
    callback({ success: false, error });
  });

  listeners.set(`course-assignments-${courseId}`, unsubscribe);
  return unsubscribe;
};

// Listen to course materials in real-time
export const listenToCourseMaterials = (courseId, callback) => {
  const q = query(
    collection(db, 'courses', courseId, 'materials'),
    orderBy('createdAt', 'desc')
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const materials = [];
    snapshot.forEach((doc) => {
      materials.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: materials });
  }, (error) => {
    callback({ success: false, error });
  });

  listeners.set(`course-materials-${courseId}`, unsubscribe);
  return unsubscribe;
};

// Listen to course discussions in real-time
export const listenToCourseDiscussions = (courseId, callback) => {
  const q = query(
    collection(db, 'courses', courseId, 'discussions'),
    orderBy('createdAt', 'desc')
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const discussions = [];
    snapshot.forEach((doc) => {
      discussions.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: discussions });
  }, (error) => {
    callback({ success: false, error });
  });

  listeners.set(`course-discussions-${courseId}`, unsubscribe);
  return unsubscribe;
};

// Listen to lecture discussions in real-time
export const listenToLectureDiscussions = (lectureId, callback) => {
  const q = query(
    collection(db, 'lectureDiscussions'),
    where('lectureId', '==', lectureId),
    orderBy('createdAt', 'desc')
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const discussions = [];
    snapshot.forEach((doc) => {
      discussions.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: discussions });
  }, (error) => {
    // Check if it's an index error
    if (error.code === 'failed-precondition' || error.message.includes('index')) {
      // Fallback to simple query without ordering
      const fallbackQuery = query(
        collection(db, 'lectureDiscussions'),
        where('lectureId', '==', lectureId)
      );
      
      const fallbackUnsubscribe = onSnapshot(fallbackQuery, (snapshot) => {
        const discussions = [];
        snapshot.forEach((doc) => {
          discussions.push({ id: doc.id, ...doc.data() });
        });
        
        // Sort in memory by createdAt
        discussions.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
          const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
          return dateB - dateA;
        });
        
        callback({ success: true, data: discussions, usedFallback: true });
      }, (fallbackError) => {
        callback({ success: false, error: fallbackError });
      });
      
      // Store the fallback unsubscribe function
      listeners.set(`lecture-discussions-fallback-${lectureId}`, fallbackUnsubscribe);
      return fallbackUnsubscribe;
    }
    
    callback({ success: false, error });
  });

  listeners.set(`lecture-discussions-${lectureId}`, unsubscribe);
  return unsubscribe;
};

// ===== REAL-TIME DOUBT DATA LISTENERS =====

// Listen to doubt responses in real-time
export const listenToDoubtResponses = (doubtId, callback) => {
  const q = query(
    collection(db, 'doubts', doubtId, 'responses'),
    orderBy('createdAt', 'asc')
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const responses = [];
    snapshot.forEach((doc) => {
      responses.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: responses });
  }, (error) => {
    callback({ success: false, error });
  });

  listeners.set(`doubt-responses-${doubtId}`, unsubscribe);
  return unsubscribe;
};

// ===== REAL-TIME SESSION DATA LISTENERS =====

// Listen to session registrations in real-time
export const listenToSessionRegistrations = (sessionId, callback) => {
  const q = query(
    collection(db, 'liveSessions', sessionId, 'registrations'),
    orderBy('registeredAt', 'asc')
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const registrations = [];
    snapshot.forEach((doc) => {
      registrations.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: registrations });
  }, (error) => {
    callback({ success: false, error });
  });

  listeners.set(`session-registrations-${sessionId}`, unsubscribe);
  return unsubscribe;
};

// ===== REAL-TIME ANALYTICS LISTENERS =====

// Listen to real-time analytics data
export const listenToAnalytics = (callback) => {
  // Listen to multiple collections for analytics
  const usersQuery = query(collection(db, COLLECTIONS.USERS));
  const testResultsQuery = query(collection(db, COLLECTIONS.TEST_RESULTS));
  const questionsQuery = query(collection(db, COLLECTIONS.QUESTION_BANK));

  const unsubscribeUsers = onSnapshot(usersQuery, (usersSnapshot) => {
    const unsubscribeTestResults = onSnapshot(testResultsQuery, (testResultsSnapshot) => {
      const unsubscribeQuestions = onSnapshot(questionsQuery, (questionsSnapshot) => {
        const analytics = {
          totalUsers: usersSnapshot.size,
          totalTests: testResultsSnapshot.size,
          totalQuestions: questionsSnapshot.size,
          activeUsers: 0, // Calculate based on recent activity
          averageScore: 0, // Calculate from test results
          completionRate: 0 // Calculate from test results
        };

        // Calculate additional metrics
        let totalScore = 0;
        let completedTests = 0;
        testResultsSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.score) {
            totalScore += data.score;
            completedTests++;
          }
        });

        if (completedTests > 0) {
          analytics.averageScore = Math.round(totalScore / completedTests);
          analytics.completionRate = Math.round((completedTests / testResultsSnapshot.size) * 100);
        }

        callback({ success: true, data: analytics });
      });
      listeners.set('analytics-questions', unsubscribeQuestions);
    });
    listeners.set('analytics-test-results', unsubscribeTestResults);
  });
  listeners.set('analytics-users', unsubscribeUsers);

  return () => {
    // Return cleanup function
    const cleanup = () => {
      listeners.get('analytics-users')?.();
      listeners.get('analytics-test-results')?.();
      listeners.get('analytics-questions')?.();
    };
    return cleanup;
  };
};

// ===== REAL-TIME NOTIFICATIONS =====

// Listen to user notifications in real-time
export const listenToUserNotifications = (userId, callback) => {
  const q = query(
    collection(db, 'users', userId, 'notifications'),
    orderBy('createdAt', 'desc'),
    limit(50)
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const notifications = [];
    snapshot.forEach((doc) => {
      notifications.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: notifications });
  }, (error) => {
    callback({ success: false, error });
  });

  listeners.set(`user-notifications-${userId}`, unsubscribe);
  return unsubscribe;
};

// Listen to user bookmarks in real-time
export const listenToUserBookmarks = (userId, callback) => {
  const q = query(
    collection(db, COLLECTIONS.BOOKMARKS),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const bookmarks = [];
    snapshot.forEach((doc) => {
      bookmarks.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: bookmarks });
  }, (error) => {
    callback({ success: false, error });
  });

  listeners.set(`user-bookmarks-${userId}`, unsubscribe);
  return unsubscribe;
};

// Listen to user courses in real-time
export const listenToUserCourses = (userId, callback) => {
  const q = query(
    collection(db, COLLECTIONS.USER_COURSES),
    where('userId', '==', userId),
    orderBy('enrolledAt', 'desc')
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const userCourses = [];
    snapshot.forEach((doc) => {
      userCourses.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: userCourses });
  }, (error) => {
    callback({ success: false, error });
  });

  listeners.set(`user-courses-${userId}`, unsubscribe);
  return unsubscribe;
};

// ===== REAL-TIME ADMIN ACTIONS =====

// Real-time admin action logger
export const logAdminAction = async (adminId, action, details) => {
  try {
    const actionLog = {
      adminId,
      action,
      details,
      timestamp: new Date(),
      ip: 'admin-panel' // You can add real IP detection
    };

    await addDoc(collection(db, 'adminActions'), actionLog);
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Listen to admin logs by email
export const listenToAdminLogsByEmail = (adminEmail, callback) => {
  // This would need to query users collection first to get userId, then query adminActions
  // For now, we'll use the getAdminLogs function in collections.js
  return () => {}; // Return empty unsubscribe function
};

// Listen to admin actions in real-time
export const listenToAdminActions = (callback) => {
  const q = query(
    collection(db, 'adminActions'),
    orderBy('timestamp', 'desc'),
    limit(100)
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const actions = [];
    snapshot.forEach((doc) => {
      actions.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: actions });
  }, (error) => {
    callback({ success: false, error });
  });

  listeners.set('admin-actions', unsubscribe);
  return unsubscribe;
};

// ===== POLL REAL-TIME LISTENERS =====

// Listen to all polls in real-time
export const listenToAllPolls = (callback) => {
  const q = query(
    collection(db, COLLECTIONS.POLLS),
    orderBy('createdAt', 'desc')
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const polls = [];
    snapshot.forEach((doc) => {
      polls.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: polls });
  }, (error) => {
    callback({ success: false, error });
  });

  listeners.set('all-polls', unsubscribe);
  return unsubscribe;
};

// Listen to poll votes in real-time
export const listenToPollVotes = (pollId, callback) => {
  const q = query(
    collection(db, COLLECTIONS.POLL_VOTES),
    where('pollId', '==', pollId)
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const votes = [];
    snapshot.forEach((doc) => {
      votes.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: votes });
  }, (error) => {
    callback({ success: false, error });
  });

  listeners.set(`poll-votes-${pollId}`, unsubscribe);
  return unsubscribe;
};

// ===== BATCH OPERATIONS FOR REAL-TIME UPDATES =====

// Batch update multiple documents for real-time consistency
export const batchUpdateForRealTime = async (updates) => {
  try {
    const batch = writeBatch(db);
    
    updates.forEach(({ path, data, operation = 'set' }) => {
      const docRef = doc(db, ...path);
      if (operation === 'set') {
        batch.set(docRef, data, { merge: true });
      } else if (operation === 'update') {
        batch.update(docRef, data);
      } else if (operation === 'delete') {
        batch.delete(docRef);
      }
    });

    await batch.commit();
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Transaction for complex real-time updates
export const transactionUpdateForRealTime = async (updateFunction) => {
  try {
    const result = await runTransaction(db, updateFunction);
    return { success: true, data: result };
  } catch (error) {
    return { success: false, error };
  }
};

// ===== CLEANUP FUNCTIONS =====

// Clean up specific listener
export const cleanupListener = (listenerKey) => {
  const listener = listeners.get(listenerKey);
  if (listener) {
    listener();
    listeners.delete(listenerKey);
    }
};

// Clean up all listeners
export const cleanupAllListeners = () => {
  listeners.forEach((listener, key) => {
    listener();
    });
  listeners.clear();
  };

// Clean up listeners for a specific user
export const cleanupUserListeners = (userId) => {
  const keysToRemove = [];
  listeners.forEach((listener, key) => {
    if (key.includes(userId)) {
      listener();
      keysToRemove.push(key);
    }
  });
  
  keysToRemove.forEach(key => {
    listeners.delete(key);
    });
};

// ===== UTILITY FUNCTIONS =====

// Get active listeners count
export const getActiveListenersCount = () => {
  return listeners.size;
};

// Get active listeners keys
export const getActiveListenersKeys = () => {
  return Array.from(listeners.keys());
};

// Check if a specific listener is active
export const isListenerActive = (listenerKey) => {
  return listeners.has(listenerKey);
};

export default {
  // User listeners
  listenToUserDashboard,
  listenToUserTestResults,
  listenToUserNotes,
  listenToUserDoubts,
  listenToUserActivities,
  listenToUserNotifications,
  
  // Admin listeners
  listenToAllUsers,
  listenToAllTestResults,
  listenToAllQuestions,
  listenToAllCourses,
  listenToAllDoubts,
  listenToAdminActions,
  
  // Poll listeners
  listenToAllPolls,
  listenToPollVotes,
  
  // Course listeners
  listenToCourseAssignments,
  listenToCourseMaterials,
  listenToCourseDiscussions,
  listenToLectureDiscussions,
  
  // Doubt listeners
  listenToDoubtResponses,
  
  // Session listeners
  listenToSessionRegistrations,
  
  // Analytics listeners
  listenToAnalytics,
  
  // Admin actions
  logAdminAction,
  
  // Batch operations
  batchUpdateForRealTime,
  transactionUpdateForRealTime,
  
  // Cleanup functions
  cleanupListener,
  cleanupAllListeners,
  cleanupUserListeners,
  
  // Utility functions
  getActiveListenersCount,
  getActiveListenersKeys,
  isListenerActive
};
